import React, { useState } from 'react';
import { IconListFilter } from '@iwoca/icons';
import { Button, Spinner } from '@iwoca/orion';
import { Trans } from '@lingui/macro';
import clsx from 'clsx';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { TNotificationsData } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { EmptyNotifications } from './EmptyNotifications/EmptyNotifications';
import { NotificationSettings } from './NoticationSettings/NotificationSettings';
import { Notification } from './Notification/Notification';
import {
  groupNotificationsByDays,
  hasFilters,
} from './NotificationsContainer.helpers';
import styles from './NotificationsContainer.module.css';

type TProps = {
  notificationsData: TNotificationsData;
  containerRef?: React.RefObject<HTMLDivElement>;
  onCloseContainer?: () => void;
};

export const NotificationsContainer = ({
  notificationsData,
  containerRef,
  onCloseContainer,
}: TProps) => {
  const [showSettings, setShowSettings] = useState(false);
  const {
    isLoading,
    isShowingMore,
    handleShowMore,
    notifications,
    hasMoreNotifications,
    markAllAsRead,
    markItemAsRead,
    hasData,
    isMarkingAsRead,
    subscriptions,
    isUpdatingSubscriptions,
    updateSubscriptions,
  } = notificationsData;

  const { i18n } = useLocale();
  const { trackEvent } = useAmplitude();

  const handleOnMarkAsRead = (notificationId: string) => {
    markItemAsRead(notificationId);
    onCloseContainer?.();
  };

  const toggleSettings = () => setShowSettings(!showSettings);

  const handleMarkAsReadClick = () => {
    trackEvent(AMPLITUDE_EVENTS.CTAClicked, {
      'CTA type': 'Button',
      'CTA text': 'Mark all as read',
    });
    markAllAsRead();
  };

  return (
    <div
      data-testid="notifications-container"
      className={clsx(styles.notificationsContainer, {
        [styles.showingMore]: isShowingMore,
      })}
    >
      <NotificationSettings
        show={showSettings}
        onBackClick={toggleSettings}
        subscriptions={subscriptions}
        isUpdatingSubscriptions={isUpdatingSubscriptions}
        updateSubscriptions={updateSubscriptions}
      />
      <div className={styles.topSection}>
        <div>
          {hasData && (
            <Button
              variant="tertiary"
              onClick={handleMarkAsReadClick}
              className={styles.readButton}
              disabled={isMarkingAsRead}
            >
              <Trans>Mark all as read</Trans>
              {isMarkingAsRead && <Spinner />}
            </Button>
          )}
        </div>
        <Button
          iconOnly
          className={clsx(styles.settingsButton, {
            [styles.hasFilters]: hasFilters(subscriptions),
          })}
          variant="tertiary"
          size="small"
          onClick={toggleSettings}
        >
          <IconListFilter fontSize="1.2rem" className={styles.filterIcon} />
        </Button>
      </div>
      <div className={styles.notificationsList} ref={containerRef}>
        {!hasData ? (
          <EmptyNotifications />
        ) : (
          groupNotificationsByDays(notifications, i18n).map(
            ({ date, notifications }) => (
              <div key={date}>
                <span className={styles.date}>{date}</span>

                {notifications.map((notification) => (
                  <Notification
                    onMarkAsRead={handleOnMarkAsRead}
                    notification={notification}
                    key={notification.notification_id}
                  />
                ))}
              </div>
            ),
          )
        )}
      </div>
      {isLoading && (
        <div data-testid="notifications-loader" className={styles.spinner}>
          <Spinner />
        </div>
      )}
      {!isShowingMore && hasData && hasMoreNotifications && (
        <div className={styles.bottomSection}>
          <Button
            className={styles.showMoreButton}
            variant="tertiary"
            onClick={handleShowMore}
          >
            <Trans>See more</Trans>
          </Button>
        </div>
      )}
    </div>
  );
};
